import * as THREE from 'three';
import { GlowLayer } from './glow.ts';

interface Particle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  life: number;
  max: number;
  size: number;
  grow: number;
  color: THREE.Color;
  alpha: number;
  layer: 0 | 1;
  gravity: number;
}

interface Ring {
  x: number;
  y: number;
  z: number;
  r0: number;
  r1: number;
  life: number;
  max: number;
  color: THREE.Color;
  alpha: number;
}

interface Beam {
  a: THREE.Vector3;
  b: THREE.Vector3;
  life: number;
  max: number;
  color: THREE.Color;
  width: number;
  jag: number;
  soft: boolean;
}

/** A soft beam: bright core, sides fading to nothing, and both ends tapering (shafts of light, not flat bars). */
const softTexture = (): THREE.Texture => {
  const W = 64, H = 64;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  const img = g.createImageData(W, H);
  for (let y = 0; y < H; y++) {
    const v = (y + 0.5) / H;
    const end = Math.min(1, v / 0.15, (1 - v) / 0.15);
    for (let x = 0; x < W; x++) {
      const u = Math.abs((x + 0.5) / W - 0.5) * 2;
      const across = Math.pow(Math.max(0, 1 - u), 1.8);
      const o = (y * W + x) * 4;
      img.data[o] = img.data[o + 1] = img.data[o + 2] = 255;
      img.data[o + 3] = Math.round(across * end * 255);
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

const ringTexture = (): THREE.Texture => {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const g = c.getContext('2d') as CanvasRenderingContext2D;
  const gr = g.createRadialGradient(64, 64, 40, 64, 64, 64);
  gr.addColorStop(0, 'rgba(255,255,255,0)');
  gr.addColorStop(0.75, 'rgba(255,255,255,.9)');
  gr.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
};

/**
 * Stop-motion effects: particles, ground rings and beams. `step()` advances at 12 Hz (like the sim);
 * `draw()` re-fills GPU buffers every frame so the camera can move smoothly.
 */
export class Vfx {
  readonly group = new THREE.Group();
  readonly glow = new GlowLayer(4096);
  readonly smoke = new GlowLayer(2048, THREE.NormalBlending);
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private beams: Beam[] = [];
  private ringMesh: THREE.InstancedMesh;
  private ringMat: THREE.MeshBasicMaterial;
  private beamGeo = new THREE.BufferGeometry();
  private beamPos = new Float32Array(512 * 6 * 3 * 3);
  private beamCol = new Float32Array(512 * 6 * 4 * 3);
  private beamMesh: THREE.Mesh;
  private softGeo = new THREE.BufferGeometry();
  private softPos = new Float32Array(256 * 6 * 3);
  private softCol = new Float32Array(256 * 6 * 4);
  private softUv = new Float32Array(256 * 6 * 2);
  private softMesh: THREE.Mesh;
  private m = new THREE.Matrix4();
  private c = new THREE.Color();
  private rng = 1;
  /** While set, nothing new is spawned (a redraw between ticks). */
  muted = false;
  /**
   * Until the view's compile ahead is done, the rings, beams and shafts stay in the scene with nothing to draw (no
   * draw call) so their shaders compile with the rest: the first shaft of a fight compiled on the spot otherwise.
   */
  primed = false;
  /** How far into the next 12 Hz step this frame is (0..1): sparks, smoke, rings and beams are drawn that far along. */
  alpha = 0;

  constructor() {
    // Flat on the ground: pulled toward the camera in the depth test like the other floor marks, so it doesn't flicker on the tiles.
    this.ringMat = new THREE.MeshBasicMaterial({ map: ringTexture(), transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
    this.ringMesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2), this.ringMat, 256);
    this.ringMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.ringMesh.frustumCulled = false;
    this.ringMesh.count = 0;
    // With its colour buffer from the start: the program compiled ahead is the one every ring draws with.
    this.ringMesh.setColorAt(0, new THREE.Color(0, 0, 0));
    this.beamGeo.setAttribute('position', new THREE.BufferAttribute(this.beamPos, 3).setUsage(THREE.DynamicDrawUsage));
    this.beamGeo.setAttribute('color', new THREE.BufferAttribute(this.beamCol, 4).setUsage(THREE.DynamicDrawUsage));
    this.beamMesh = new THREE.Mesh(
      this.beamGeo,
      new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true }),
    );
    this.beamMesh.frustumCulled = false;
    this.softGeo.setAttribute('position', new THREE.BufferAttribute(this.softPos, 3).setUsage(THREE.DynamicDrawUsage));
    this.softGeo.setAttribute('color', new THREE.BufferAttribute(this.softCol, 4).setUsage(THREE.DynamicDrawUsage));
    this.softGeo.setAttribute('uv', new THREE.BufferAttribute(this.softUv, 2).setUsage(THREE.DynamicDrawUsage));
    this.softMesh = new THREE.Mesh(
      this.softGeo,
      new THREE.MeshBasicMaterial({ map: softTexture(), vertexColors: true, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, forceSinglePass: true }),
    );
    this.softMesh.frustumCulled = false;
    this.group.add(this.smoke.mesh, this.glow.mesh, this.ringMesh, this.beamMesh, this.softMesh);
    this.smoke.mesh.renderOrder = 9;
  }

  private rand(): number {
    this.rng = (Math.imul(this.rng, 1103515245) + 12345) >>> 0;
    return this.rng / 4294967296;
  }

  burst(p: THREE.Vector3, o: { n: number; color: THREE.ColorRepresentation; speed?: number; up?: number; size?: number; life?: number; smoke?: boolean; gravity?: number; spread?: number }): void {
    if (this.muted) return;
    const col = new THREE.Color(o.color);
    for (let i = 0; i < o.n; i++) {
      const a = this.rand() * Math.PI * 2;
      const sp = (o.speed ?? 0.06) * (0.4 + this.rand());
      const spread = o.spread ?? 0.05;
      const life = Math.round((o.life ?? 8) * (0.7 + this.rand() * 0.6));
      this.particles.push({
        x: p.x + (this.rand() - 0.5) * spread,
        y: p.y + this.rand() * spread,
        z: p.z + (this.rand() - 0.5) * spread,
        vx: Math.cos(a) * sp,
        vy: (o.up ?? 0.05) * (0.5 + this.rand()),
        vz: Math.sin(a) * sp,
        life,
        max: life,
        size: (o.size ?? 0.18) * (0.7 + this.rand() * 0.6),
        grow: o.smoke ? 1.12 : 0.92,
        color: col,
        alpha: o.smoke ? 0.55 : 1,
        layer: o.smoke ? 1 : 0,
        gravity: o.gravity ?? (o.smoke ? -0.002 : 0.006),
      });
    }
  }

  /**
   * Motes that start on a circle of radius `r` around `p` and are sucked toward its center while they
   * swirl around it (`swirl` > 0 turns them counter-clockwise seen from above).
   */
  implode(p: THREE.Vector3, r: number, o: { n: number; color: THREE.ColorRepresentation; size?: number; life?: number; swirl?: number; lift?: number }): void {
    if (this.muted) return;
    const col = new THREE.Color(o.color);
    const life = o.life ?? 8;
    // Velocity damps by 0.9 a step: this speed covers about 85 % of the radius over the mote's life.
    const k = (0.85 * 0.1) / (1 - Math.pow(0.9, life));
    for (let i = 0; i < o.n; i++) {
      const a = this.rand() * Math.PI * 2;
      const rr = r * (0.85 + this.rand() * 0.25);
      const cx = Math.cos(a), sz = Math.sin(a);
      const sw = (o.swirl ?? 0.5) * k * rr;
      const L = Math.round(life * (0.85 + this.rand() * 0.3));
      this.particles.push({
        x: p.x + cx * rr,
        y: p.y + (o.lift ?? 0.05) * this.rand(),
        z: p.z + sz * rr,
        vx: -cx * k * rr - sz * sw,
        vy: 0,
        vz: -sz * k * rr + cx * sw,
        life: L,
        max: L,
        size: (o.size ?? 0.12) * (0.7 + this.rand() * 0.6),
        grow: 0.96,
        color: col,
        alpha: 1,
        layer: 0,
        gravity: 0,
      });
    }
  }

  ring(p: THREE.Vector3, r0: number, r1: number, color: THREE.ColorRepresentation, life = 6, alpha = 0.9): void {
    if (this.muted) return;
    this.rings.push({ x: p.x, y: p.y + 0.03, z: p.z, r0, r1, life, max: life, color: new THREE.Color(color), alpha });
  }

  beam(a: THREE.Vector3, b: THREE.Vector3, color: THREE.ColorRepresentation, life = 2, width = 0.06, jag = 0): void {
    if (this.muted) return;
    this.beams.push({ a: a.clone(), b: b.clone(), life, max: life, color: new THREE.Color(color), width, jag, soft: false });
  }

  /** A shaft of light: like `beam`, but its edges fade out (and it can be much wider without looking like a bar). */
  shaft(a: THREE.Vector3, b: THREE.Vector3, color: THREE.ColorRepresentation, life = 6, width = 0.3): void {
    if (this.muted) return;
    this.beams.push({ a: a.clone(), b: b.clone(), life, max: life, color: new THREE.Color(color), width, jag: 0, soft: true });
  }

  /** 12 Hz step. */
  step(): void {
    const keep: Particle[] = [];
    for (const p of this.particles) {
      p.life--;
      if (p.life <= 0) continue;
      p.x += p.vx;
      p.y += p.vy;
      p.z += p.vz;
      p.vy -= p.gravity;
      p.vx *= 0.9;
      p.vz *= 0.9;
      p.size *= p.grow;
      keep.push(p);
    }
    this.particles = keep;
    this.rings = this.rings.filter((r) => --r.life > 0);
    this.beams = this.beams.filter((b) => --b.life > 0);
  }

  /** Every frame: rebuild buffers (beams face the camera). */
  draw(camera: THREE.Camera, extraGlow?: (g: GlowLayer) => void): void {
    this.glow.begin();
    this.smoke.begin();
    const a = this.alpha;
    for (const p of this.particles) {
      const t = Math.max(0, p.life - a) / p.max;
      const size = p.size * (1 + (p.grow - 1) * a);
      (p.layer === 0 ? this.glow : this.smoke).add(p.x + p.vx * a, p.y + p.vy * a, p.z + p.vz * a, p.color, size, p.alpha * Math.min(1, t * 1.6));
    }
    if (extraGlow) extraGlow(this.glow);
    this.glow.end();
    this.smoke.end();
    let n = 0;
    for (const r of this.rings) {
      if (n >= 256) break;
      const t = Math.min(1, 1 - (r.life - a) / r.max);
      const rad = r.r0 + (r.r1 - r.r0) * t;
      this.m.makeScale(rad, 1, rad).setPosition(r.x, r.y, r.z);
      this.ringMesh.setMatrixAt(n, this.m);
      this.c.copy(r.color).multiplyScalar(r.alpha * (1 - t));
      this.ringMesh.setColorAt(n, this.c);
      n++;
    }
    this.ringMesh.count = n;
    this.ringMesh.instanceMatrix.needsUpdate = true;
    if (this.ringMesh.instanceColor) this.ringMesh.instanceColor.needsUpdate = true;
    this.ringMesh.visible = n > 0 || !this.primed;
    // Beams: camera-facing quads, optionally jagged (lightning).
    const camPos = camera.position;
    let v = 0;
    const dir = new THREE.Vector3();
    const side = new THREE.Vector3();
    const toCam = new THREE.Vector3();
    const pushQuad = (a: THREE.Vector3, b: THREE.Vector3, w: number, col: THREE.Color, alpha: number): void => {
      if (v + 6 > this.beamPos.length / 3) return;
      dir.subVectors(b, a);
      toCam.subVectors(camPos, a);
      side.crossVectors(dir, toCam).normalize().multiplyScalar(w);
      const pts = [
        [a.x - side.x, a.y - side.y, a.z - side.z],
        [a.x + side.x, a.y + side.y, a.z + side.z],
        [b.x + side.x, b.y + side.y, b.z + side.z],
        [a.x - side.x, a.y - side.y, a.z - side.z],
        [b.x + side.x, b.y + side.y, b.z + side.z],
        [b.x - side.x, b.y - side.y, b.z - side.z],
      ];
      for (const p of pts) {
        this.beamPos[v * 3] = p[0] as number;
        this.beamPos[v * 3 + 1] = p[1] as number;
        this.beamPos[v * 3 + 2] = p[2] as number;
        this.beamCol[v * 4] = col.r;
        this.beamCol[v * 4 + 1] = col.g;
        this.beamCol[v * 4 + 2] = col.b;
        this.beamCol[v * 4 + 3] = alpha;
        v++;
      }
    };
    let sv = 0;
    for (const b of this.beams) {
      if (!b.soft) continue;
      if (sv + 6 > this.softPos.length / 3) break;
      const alpha = Math.max(0, b.life - a) / b.max;
      dir.subVectors(b.b, b.a);
      toCam.subVectors(camPos, b.a);
      side.crossVectors(dir, toCam).normalize().multiplyScalar(b.width);
      const pts = [
        [b.a.x - side.x, b.a.y - side.y, b.a.z - side.z, 0, 0],
        [b.a.x + side.x, b.a.y + side.y, b.a.z + side.z, 1, 0],
        [b.b.x + side.x, b.b.y + side.y, b.b.z + side.z, 1, 1],
        [b.a.x - side.x, b.a.y - side.y, b.a.z - side.z, 0, 0],
        [b.b.x + side.x, b.b.y + side.y, b.b.z + side.z, 1, 1],
        [b.b.x - side.x, b.b.y - side.y, b.b.z - side.z, 0, 1],
      ];
      for (const p of pts) {
        this.softPos[sv * 3] = p[0] as number;
        this.softPos[sv * 3 + 1] = p[1] as number;
        this.softPos[sv * 3 + 2] = p[2] as number;
        this.softUv[sv * 2] = p[3] as number;
        this.softUv[sv * 2 + 1] = p[4] as number;
        this.softCol[sv * 4] = b.color.r;
        this.softCol[sv * 4 + 1] = b.color.g;
        this.softCol[sv * 4 + 2] = b.color.b;
        this.softCol[sv * 4 + 3] = alpha;
        sv++;
      }
    }
    this.softGeo.setDrawRange(0, sv);
    (this.softGeo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.softGeo.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
    (this.softGeo.getAttribute('uv') as THREE.BufferAttribute).needsUpdate = true;
    this.softMesh.visible = sv > 0 || !this.primed;
    for (const b of this.beams) {
      if (b.soft) continue;
      const alpha = Math.max(0, b.life - a) / b.max;
      if (b.jag > 0) {
        let prev = b.a.clone();
        const segs = 6;
        for (let i = 1; i <= segs; i++) {
          const p = b.a.clone().lerp(b.b, i / segs);
          if (i < segs) p.add(new THREE.Vector3((this.rand() - 0.5) * b.jag, (this.rand() - 0.5) * b.jag, (this.rand() - 0.5) * b.jag));
          pushQuad(prev, p, b.width, b.color, alpha);
          prev = p;
        }
      } else pushQuad(b.a, b.b, b.width, b.color, alpha);
    }
    this.beamGeo.setDrawRange(0, v);
    (this.beamGeo.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    (this.beamGeo.getAttribute('color') as THREE.BufferAttribute).needsUpdate = true;
    this.beamMesh.visible = v > 0 || !this.primed;
  }
}
